import type { Combo } from '@/types/combo'

/** Mesmos presets fixos de `backend/public/js/widget.js`. */
export const WIDGET_AVULSO_PRESETS = [50, 100, 200, 500, 1000] as const

export function parseApiMoney(value: number | string | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace(',', '.').trim())
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export function parseQuantityField(raw: string | undefined, fallback: number | null): number | null {
  if (raw === undefined || raw === null) return fallback
  const trimmed = String(raw).trim()
  if (trimmed === '') return fallback
  const n = Number.parseInt(trimmed, 10)
  if (!Number.isFinite(n)) return fallback
  return n
}

/**
 * Preço unitário exibido na prévia: prioriza o valor digitado na aba Valores (ainda não salvo).
 */
export function resolveWidgetUnitPrice(
  livePriceRaw: string | undefined,
  apiPrice: number | string | null | undefined,
): number {
  if (livePriceRaw !== undefined && String(livePriceRaw).trim() !== '') {
    return parseApiMoney(livePriceRaw)
  }
  return parseApiMoney(apiPrice)
}

/** Igual ao widget: mínimo efetivo para avulsos é pelo menos 1. */
export function resolveWidgetMinExtra(
  liveRaw: string | undefined,
  apiValue: number | null | undefined,
): number {
  const fromLive = parseQuantityField(liveRaw, null)
  const base = fromLive !== null ? fromLive : (apiValue ?? 1)
  return Math.max(1, base)
}

/** Null = sem limite superior (como no widget quando max não está definido). */
export function resolveWidgetMaxExtra(
  liveRaw: string | undefined,
  apiValue: number | null | undefined,
): number | null {
  const fromLive = parseQuantityField(liveRaw, null)
  const v = fromLive !== null ? fromLive : apiValue
  if (v == null || !Number.isFinite(Number(v)) || Number(v) <= 0) {
    return null
  }
  return Math.floor(Number(v))
}

export function clampWidgetExtraNumbers(value: number, min: number, max: number | null): number {
  if (max !== null) {
    return Math.min(Math.max(value, min), max)
  }
  return Math.max(value, min)
}

/** Paridade com `isComboVisible` do widget (inclui 1 e '1'). */
export function isComboVisibleInWidget(combo: Pick<Combo, 'is_visible'>): boolean {
  const v = combo.is_visible as boolean | number | string
  return v === true || v === 1 || v === '1'
}

/** Espelha o cálculo de `CombosService::createCombo` / update com desconto percentual. */
export function computeComboDisplayPrice(
  unitPrice: number,
  quantity: number,
  discountPercentage: number,
): number {
  const q = Number.isFinite(quantity) && quantity > 0 ? quantity : 0
  const disc = Number.isFinite(discountPercentage) ? discountPercentage : 0
  const fullPrice = Math.round(unitPrice * q * 100) / 100
  return Math.round(fullPrice * (1 - disc / 100) * 100) / 100
}

export function formatWidgetBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

/** Compara valores monetários com tolerância para flutuante. */
export function moneyValuesClose(a: number, b: number, epsilon = 0.005): boolean {
  return Math.abs(a - b) < epsilon
}

export function widgetLimitsLabel(min: number, max: number | null): string {
  if (max !== null) {
    return `Min: ${min} | Max: ${max}`
  }
  return `Min: ${min}`
}
